import { create } from 'zustand'
import type { Flashcard, MasteryLevel } from '../../../shared/types'
import * as api from '../services/api'

type CacheKey = string

export interface FlashcardListResponse {
  flashcards: Flashcard[]
  totalCount: number
  masteryBreakdown: Record<string, number>
  limit: number
  offset: number
}

interface CacheEntry {
  timestamp: number
  data: FlashcardListResponse
}

interface FlashcardCacheState {
  entries: Record<CacheKey, CacheEntry>
  getEntry: (key: CacheKey) => CacheEntry | undefined
  setEntry: (key: CacheKey, data: FlashcardListResponse) => void
  clear: () => void
}

const STORAGE_PREFIX = 'flashcard-cache:'
const STORAGE_MAX_AGE = 5 * 60 * 1000 // Keep session cache for up to 5 minutes
export const FLASHCARD_CACHE_TTL = 60 * 1000 // Skip refetching if cache is newer than this

const safeSessionStorage = () => {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

const hydrateFromStorage = (): Record<CacheKey, CacheEntry> => {
  const storage = safeSessionStorage()
  if (!storage) return {}

  const entries: Record<CacheKey, CacheEntry> = {}
  for (let i = 0; i < storage.length; i++) {
    const storageKey = storage.key(i)
    if (!storageKey || !storageKey.startsWith(STORAGE_PREFIX)) continue
    try {
      const value = storage.getItem(storageKey)
      if (!value) continue
      const parsed = JSON.parse(value) as CacheEntry
      if (parsed?.timestamp && parsed?.data) {
        const age = Date.now() - parsed.timestamp
        if (age <= STORAGE_MAX_AGE) {
          entries[storageKey.replace(STORAGE_PREFIX, '')] = parsed
        } else {
          storage.removeItem(storageKey)
        }
      }
    } catch {
      storage.removeItem(storageKey)
    }
  }
  return entries
}

const persistEntry = (key: CacheKey, entry: CacheEntry) => {
  const storage = safeSessionStorage()
  if (!storage) return
  try {
    storage.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify(entry))
  } catch {
    // Ignore storage quota errors
  }
}

export const buildFlashcardCacheKey = (input: {
  userId: string
  targetLanguage?: string | null
  masteryLevel: MasteryLevel | 'all'
  limit: number
}): CacheKey => {
  const lang = input.targetLanguage ?? 'all'
  return `u:${input.userId}|lang:${lang}|mastery:${input.masteryLevel}|limit:${input.limit}`
}

export const useFlashcardCacheStore = create<FlashcardCacheState>((set, get) => ({
  entries: hydrateFromStorage(),
  getEntry: (key) => get().entries[key],
  setEntry: (key, data) => {
    const entry: CacheEntry = { timestamp: Date.now(), data }
    set((state) => {
      const updated = { ...state.entries, [key]: entry }
      return { entries: updated }
    })
    persistEntry(key, entry)
  },
  clear: () => {
    set({ entries: {} })
    const storage = safeSessionStorage()
    if (!storage) return
    const keysToRemove: string[] = []
    for (let i = 0; i < storage.length; i++) {
      const storageKey = storage.key(i)
      if (storageKey?.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(storageKey)
      }
    }
    keysToRemove.forEach((key) => storage.removeItem(key))
  },
}))

export async function prefetchFlashcardList(input: {
  userId: string
  targetLanguage?: string | null
  masteryLevel?: MasteryLevel | 'all'
  limit?: number
}) {
  const userId = input.userId
  if (!userId) return

  const limit = input.limit ?? 100
  const masteryLevel = input.masteryLevel ?? 'all'
  const targetLanguage = input.targetLanguage ?? undefined
  const cacheKey = buildFlashcardCacheKey({
    userId,
    targetLanguage,
    masteryLevel,
    limit,
  })

  const now = Date.now()
  const existing = useFlashcardCacheStore.getState().getEntry(cacheKey)
  if (existing && now - existing.timestamp <= FLASHCARD_CACHE_TTL / 2) {
    return existing.data
  }

  try {
    const data = await api.getFlashcards(userId, {
      limit,
      offset: 0,
      masteryLevel: masteryLevel === 'all' ? undefined : masteryLevel,
      targetLanguage: targetLanguage ?? undefined,
    })
    useFlashcardCacheStore.getState().setEntry(cacheKey, data)
    return data
  } catch (err) {
    console.warn('[FlashcardCache] Prefetch failed', err)
    return existing?.data
  }
}
